(ns ui.components.filter.util
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.entity :as entity]
   [common.entity-utils :as eu]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]))

(def id-filters
  [:filter-operator/is-known
   :filter-operator/is-unknown
   :filter-operator/equals])

(def boolean-filters
  [:filter-operator/is-known
   :filter-operator/is-unknown
   :filter-operator/is-true
   :filter-operator/is-false])

(def numeric-filters
  [:filter-operator/is-known
   :filter-operator/is-unknown
   :filter-operator/greater-than
   :filter-operator/smaller-than
   :filter-operator/equals
   :filter-operator/equal-or-greater-than
   :filter-operator/equal-or-smaller-than
   :filter-operator/between-exclusive
   :filter-operator/between-inclusive])

(def text-filters
  [:filter-operator/is-known
   :filter-operator/is-unknown
   :filter-operator/starts-with
   :filter-operator/ends-with
   :filter-operator/text-contains
   :filter-operator/text-contains-ignore-case
   :filter-operator/text-does-not-contain
   :filter-operator/text-does-not-contain-ignore-case
   :filter-operator/text-equals
   :filter-operator/text-equals-ignore-case])

(def date-filters
  [:filter-operator/is-known
   :filter-operator/is-unknown
   :filter-operator/equals
   :filter-operator/not-equal
   :filter-operator/date-after
   :filter-operator/date-before
   :filter-operator/date-on-or-after
   :filter-operator/date-on-or-before
   :filter-operator/date-between])

(defn option-filters
  [multi?]
  (concat [:filter-operator/is-known
           :filter-operator/is-unknown
           :filter-operator/matches-none-of
           :filter-operator/matches-any-of]
          (if multi?
            [:filter-operator/matches-all-of]
            [:filter-operator/equals])))

(defn data-type->operators
  [data-type]
  (case data-type
    :data.type/boolean boolean-filters
    :data.type/currency-symbol (option-filters false)
    :data.type/currency-value numeric-filters
    :data.type/date date-filters
    :data.type/datetime date-filters
    :data.type/email text-filters
    :data.type/email-multi text-filters
    :data.type/financials text-filters
    :data.type/id id-filters
    :data.type/integer numeric-filters
    :data.type/keyword (option-filters false)
    :data.type/keyword-multi (option-filters true)
    :data.type/long numeric-filters
    :data.type/multiplier numeric-filters
    :data.type/number numeric-filters
    :data.type/percentage numeric-filters
    :data.type/ref-multi (option-filters true)
    :data.type/ref-single (option-filters false)
    :data.type/tesi-financials text-filters
    :data.type/tesi-financials-2 text-filters
    :data.type/text text-filters
    :data.type/text-edn text-filters
    :data.type/text-multi text-filters
    :data.type/text-multiline text-filters
    :data.type/text-richtext text-filters
    :data.type/text-simple text-filters
    :data.type/text-url text-filters
    nil nil))

(defn data-type->default-operators
  [data-type]
  (case data-type
    :data.type/boolean :filter-operator/is-false
    :data.type/currency-symbol :filter-operator/matches-any-of
    :data.type/currency-value :filter-operator/greater-than
    :data.type/date :filter-operator/date-after
    :data.type/datetime :filter-operator/date-after
    :data.type/email :filter-operator/starts-with
    :data.type/email-multi :filter-operator/starts-with
    :data.type/financials :filter-operator/text-contains
    :data.type/id :filter-operator/equals
    :data.type/integer :filter-operator/greater-than
    :data.type/keyword :filter-operator/matches-any-of
    :data.type/keyword-multi :filter-operator/matches-any-of
    :data.type/long :filter-operator/greater-than
    :data.type/multiplier :filter-operator/greater-than
    :data.type/number :filter-operator/greater-than
    :data.type/percentage :filter-operator/greater-than
    :data.type/ref-multi :filter-operator/matches-any-of
    :data.type/ref-single :filter-operator/matches-any-of
    :data.type/tesi-financials :filter-operator/starts-with
    :data.type/tesi-financials-2 :filter-operator/starts-with
    :data.type/text :filter-operator/starts-with
    :data.type/text-edn :filter-operator/starts-with
    :data.type/text-multi :filter-operator/starts-with
    :data.type/text-multiline :filter-operator/starts-with
    :data.type/text-richtext :filter-operator/starts-with
    :data.type/text-simple :filter-operator/starts-with
    :data.type/text-url :filter-operator/starts-with
    nil nil))

(defn filter!
  [filter entity]
  (let [{:keys [filter/key-path filter/operator]} filter
        [from-value to-value] (:filter/value filter)
        value (eu/get-value @session/tenant @session/tenant-schema entity key-path session/get-entity session/reverse-lookup)]
    (case operator
      :filter-operator/is-known (not (nil? value))
      :filter-operator/is-unknown (nil? value)
      :filter-operator/is-false (false? value)
      :filter-operator/is-true (true? value)
      :filter-operator/equals (= value from-value)
      :filter-operator/not-equal (not= value from-value)
      :filter-operator/smaller-than (< value from-value)
      :filter-operator/equal-or-smaller-than (<= value from-value)
      :filter-operator/greater-than (> value from-value)
      :filter-operator/equal-or-greater-than (>= value from-value)
      :filter-operator/between-inclusive (<= from-value value to-value)
      :filter-operator/between-exclusive (< from-value value to-value)
      :filter-operator/date-before (< value from-value)
      :filter-operator/date-after (> value from-value)
      :filter-operator/date-on-or-before (<= value from-value)
      :filter-operator/date-on-or-after (>= value from-value)
      :filter-operator/date-between (<= from-value value to-value)
      :filter-operator/starts-with (string/starts-with? (str value) from-value)
      :filter-operator/ends-with (string/ends-with? (str value) from-value)
      :filter-operator/text-equals (= value from-value)
      :filter-operator/text-equals-ignore-case (= (cu/str-trim-lcase value) (cu/str-trim-lcase from-value))
      :filter-operator/text-does-not-contain (not (string/includes? (str value) from-value))
      :filter-operator/text-does-not-contain-ignore-case (not (string/includes? (cu/str-trim-lcase value) (cu/str-trim-lcase from-value)))
      :filter-operator/text-contains (string/includes? value from-value)
      :filter-operator/text-contains-ignore-case (string/includes? (cu/str-trim-lcase value) (cu/str-trim-lcase from-value))
      :filter-operator/matches-none-of (not-any? (set from-value)
                                                 (if (coll? value)
                                                   value
                                                   [value]))
      :filter-operator/matches-any-of (some (set from-value)
                                            (if (coll? value)
                                              value
                                              [value]))
      :filter-operator/matches-all-of (every? (set from-value)
                                              (if (coll? value)
                                                value
                                                [value]))
      (js/console.error "No such filter " filter)
      )))

(defn option-value->label
  [value options]
  (->> options
       (keep (fn [opt]
               (when (or (= value (:key opt))
                         (= value (:value opt)))
                 (:label opt))))
       (first)))

(defn resolve-keyword
  [field-schema value]
  (if-let [get-label (:field/get-label field-schema)]
    (get-label value)
    (if-let [options (:options field-schema)]
      (option-value->label value options)
      (loc/tr :cannot-resolve-keyword value))))

(defn to-display-string
  [field-schema filter value]
  (let [res (cond
              (nil? value)
              nil
              (or (list? value)
                  (set? value)
                  (vector? value))
              (if (> (count value) 2)
                (loc/tr :filter-multiple-values [(count value)])
                (->> value
                     (map (partial to-display-string field-schema filter))
                     (string/join ", ")))
              :else
              (let [r (case (:filter/data-type filter)
                        :data.type/boolean (cond (true? value)
                                                 (loc/tr :true)
                                                 (false? value)
                                                 (loc/tr :false)
                                                 :else
                                                 nil)
                        :data.type/currency-symbol (currencies/get-label value)
                        :data.type/currency-value (cu/format "%.2f" value)
                        :data.type/date (fu/format-date-ts value)
                        :data.type/datetime (fu/format-date-ts value)
                        :data.type/email value
                        :data.type/email-multi value
                        :data.type/financials nil
                        :data.type/id (str value)
                        :data.type/integer (str value)
                        :data.type/keyword (resolve-keyword field-schema value)
                        :data.type/keyword-multi (resolve-keyword field-schema value)
                        :data.type/long (str value)
                        :data.type/multiplier (str value "k")
                        :data.type/number (cu/format "%.2f" value)
                        :data.type/percentage (cu/format "%.2%" value)
                        :data.type/ref-multi (some->> value
                                                      (session/get-entity)
                                                      (entity/display-string))
                        :data.type/ref-single (some->> value
                                                       (session/get-entity)
                                                       (entity/display-string))
                        :data.type/tesi-financials nil
                        :data.type/tesi-financials-2 nil
                        :data.type/text (str value)
                        :data.type/text-edn nil
                        :data.type/text-multi (str value)
                        :data.type/text-multiline (str value)
                        :data.type/text-richtext (str value)
                        :data.type/text-simple (str value)
                        :data.type/text-url (str value)
                        (str "Unknown data-type " filter))]
                r))]
    res))

(defn resolve-field-filter
  [tenant-schema options filter]
  (let [{:keys [filter/key-path filter/get-options]} filter
        filter-path-str (->> key-path
                             (butlast)
                             (map (fn [k]
                                    (let [field-schema (registry/get-field-schema tenant-schema k)]
                                      (loc/field-name k field-schema))))
                             (string/join " - "))
        last-key (last key-path)
        field-schema (registry/get-field-schema tenant-schema last-key)
        data-type (:field/data-type field-schema)
        filter-label (cu/str-trim (str filter-path-str
                                       (when-not (cu/empty-value? filter-path-str)
                                         " - ")
                                       (loc/field-name last-key field-schema)))
        options (seq (or options (:options field-schema)))]
    (merge filter
           {:filter/label            filter-label
            :filter/data-type        data-type
            :filter/operator         (data-type->default-operators data-type)
            :filter-operator-options (->> (data-type->operators data-type)
                                          (mapv (fn [oper]
                                                  {:value oper
                                                   :label (loc/tr oper)})))}
           (cond options
                 {:filter/options (->> options
                                       (map (fn [opt]
                                              {:value (or (:key opt)
                                                          (:value opt))
                                               :label (or (:label opt)
                                                          (some->> opt
                                                                   :translations
                                                                   :en))})))}
                 (some #{data-type} [:data.type/ref-single :data.type/ref-multi
                                     :data.type/keyword :data.type/keyword-multi])
                 {:filter/get-options get-options}))))


(defn resolve-filter-value
  [tenant tenant-schema {:keys [filter/key-path filter/operator] :as filter}]
  (let [filter-path-str (->> key-path
                             (butlast)
                             (map (fn [k]
                                    (let [field-schema (registry/get-field-schema tenant-schema k)]
                                      (loc/field-name k field-schema))))
                             (string/join " - "))
        last-key (last key-path)
        field-schema (registry/get-field-schema tenant-schema last-key)
        filter-label (cu/str-trim (str filter-path-str
                                       (when-not (cu/empty-value? filter-path-str)
                                         " - ")
                                       (loc/field-name last-key field-schema)))
        [from-value _to-value] (:filter/value filter)
        filter-value-label (cond (some #{operator} [:filter-operator/is-known :filter-operator/is-unknown :filter-operator/is-true :filter-operator/is-false])
                                 nil
                                 (some #{operator} [:filter-operator/matches-any-of :filter-operator/matches-all-of :filter-operator/matches-none-of])
                                 (let [values (->> from-value
                                                   (mapv (fn [single-value]
                                                           (eu/get-value tenant tenant-schema (assoc-in {} key-path single-value) key-path identity nil)))
                                                   (to-display-string field-schema filter))]
                                   (str values))
                                 :else
                                 (let [from-value (eu/get-value tenant tenant-schema (assoc-in {} key-path from-value) key-path identity nil)
                                       to-value (second (:filter/value filter))
                                       to-value (when to-value
                                                  (eu/get-value tenant tenant-schema (assoc-in {} key-path to-value) key-path identity nil))]
                                   (str (to-display-string field-schema filter from-value)
                                        (when to-value
                                          " - ")
                                        (when to-value
                                          (to-display-string field-schema filter to-value)
                                          ))))]
    (assoc filter
      :filter/label filter-label
      :filter-value-label filter-value-label
      :filter-active? true
      :filter-operator-label (loc/tr (:filter/operator filter)))))

(defn default-operator
  [data-type]
  (case data-type
    :data.type/boolean
    :filter-operator/is-true
    :data.type/currency-symbol
    :filter-operator/matches-any-of
    :data.type/currency-value
    :filter-operator/greater-than
    :data.type/date
    :filter-operator/date-after
    :data.type/datetime
    :filter-operator/date-after
    :data.type/email
    :filter-operator/starts-with
    :data.type/email-multi
    :filter-operator/starts-with
    :data.type/financials
    :filter-operator/text-contains
    :data.type/id
    :filter-operator/text-contains
    :data.type/integer
    :filter-operator/greater-than
    :data.type/keyword
    :filter-operator/matches-any-of
    :data.type/keyword-multi
    :filter-operator/matches-any-of
    :data.type/long
    :filter-operator/greater-than
    :data.type/multiplier
    :filter-operator/greater-than
    :data.type/number
    :filter-operator/greater-than
    :data.type/percentage
    :filter-operator/greater-than
    :data.type/ref-multi
    :filter-operator/matches-any-of
    :data.type/ref-single
    :filter-operator/matches-any-of
    :data.type/tesi-financials
    :filter-operator/text-contains
    :data.type/tesi-financials-2
    :filter-operator/text-contains
    :data.type/text
    :filter-operator/starts-with
    :data.type/text-edn
    :filter-operator/text-contains
    :data.type/text-multi
    :filter-operator/starts-with
    :data.type/text-multiline
    :filter-operator/starts-with
    :data.type/text-richtext
    :filter-operator/text-contains
    :data.type/text-simple
    :filter-operator/starts-with
    :data.type/text-url
    :filter-operator/text-contains
    (str "Unknown data-type " filter)))
